import { useCallback } from 'react';

import {
  AxisAlignedDimensionsIcon,
  Divider,
  MeasureIcon,
  OptimalBoundingBoxIcon,
  OutlineCircleQuestionIcon,
  PMIIcon,
  ResetViewIcon,
  Tooltip,
} from '@frontend/legacy-components';

import {
  ViewerButton,
  ViewerButtonTooltip,
  ViewerPopoverButton,
} from 'modules/common/viewer/components';

import { isMobileBrowser } from 'utils/browserUtils';

import { ViewerToolsBar } from '../components/ViewerToolsBar';
import { useDxfViewerSession } from './DxfViewerContext';
import { DxfDisplayOptionsPopover } from './components/DxfDisplayOptionsPopover';
import { DxfMappingAlertDialog } from './components/DxfMappingAlertDialog';
import { DxfMappingToolbar } from './components/DxfMappingToolbar';
import { DxfSelectionModeGroup } from './components/DxfSelectionModeGroup';
import { DxfViewerControlsInfoRows } from './components/DxfViewerControlsInfoRows';
import { useDxfMappingActions } from './hooks/useDxfMappingActions';
import { useDxfMappingValidation } from './hooks/useDxfMappingValidation';
import { useDxfSelectionMode } from './hooks/useDxfSelectionMode';
import type { OverlayKey } from './session/types';
import { useDxfLoad, useReadableStore } from './session/useSessionState';

export const DxfViewerTools = () => {
  const isMobile = isMobileBrowser();
  const session = useDxfViewerSession();

  // Discrete subscriptions: this toolbar re-renders when a person changes
  // something, and never while the drawing is being panned or zoomed.
  const load = useDxfLoad(session);
  const isSceneReady = load.status === 'ready';
  const hasPmi = isSceneReady ? load.model.hasPmi : false;
  const tool = useReadableStore(session.tools);
  const overlays = useReadableStore(session.overlays);
  const mode = useReadableStore(session.mode);
  const mappingActions = useDxfMappingActions(session);
  const selectionMode = useDxfSelectionMode();

  // Runs the live mapping validation and syncs the result onto the session.
  useDxfMappingValidation(session);

  const measureTool = useCallback(
    () => session.tools.set('measure'),
    [session],
  );

  const toggleOverlay = useCallback(
    (key: OverlayKey) => session.overlays.toggle(key),
    [session],
  );

  const onResetView = useCallback(() => {
    session.viewport.reset();
  }, [session]);

  // Mapping replaces the viewing chrome rather than adding to it: measure, axis
  // dimensions and the bounding box are scoped to viewing mode by the spec, and
  // `modeController` has already switched the overlays off on the way in.
  if (mode === 'mapping') {
    return (
      <>
        <DxfMappingToolbar
          onCancel={mappingActions.onCancel}
          onSave={mappingActions.onSave}
          isSaving={mappingActions.isSaving}
        />
        <DxfMappingAlertDialog
          isOpen={mappingActions.discardDialog.isOpen}
          onClose={mappingActions.discardDialog.onClose}
          onConfirm={mappingActions.discardDialog.onConfirm}
        />
      </>
    );
  }

  return (
    <ViewerToolsBar pl={0.75}>
      <DxfSelectionModeGroup
        {...selectionMode}
        isDisabled={!isSceneReady}
        disabledReason={isSceneReady ? undefined : 'No drawing loaded'}
      />
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Measure"
            secondLine="Click two points; snaps to ends, midpoints and centres"
          />
        }
      >
        <ViewerButton
          isActive={tool === 'measure'}
          aria-label="Measure"
          onClick={measureTool}
          disabled={!isSceneReady}
        >
          <MeasureIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      <Divider orientation="vertical" h={4} />
      {/* Axis Aligned Dimensions Tool */}
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Axis aligned dimensions"
            secondLine="Overall width and height of visible geometry"
          />
        }
      >
        <ViewerButton
          isActive={overlays.axisDimensions}
          aria-label="Axis aligned dimensions"
          onClick={() => toggleOverlay('axisDimensions')}
          disabled={!isSceneReady}
        >
          <AxisAlignedDimensionsIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      {/* Optimal Bounding Box Tool */}
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Optimal bounding box dimensions"
            secondLine="Smallest-area outline of visible geometry"
          />
        }
      >
        <ViewerButton
          isActive={overlays.optimalBoundingBox}
          aria-label="Optimal bounding box dimensions"
          onClick={() => toggleOverlay('optimalBoundingBox')}
          disabled={!isSceneReady}
        >
          <OptimalBoundingBoxIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      {/* PMI — disabled, and saying why, when the file carries none. */}
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Dimensions and annotation"
            secondLine={
              hasPmi
                ? 'The drawing’s own dimension entities'
                : 'This file carries no dimension entities'
            }
          />
        }
      >
        <ViewerButton
          isActive={overlays.pmi}
          aria-label="Dimensions and annotation"
          onClick={() => toggleOverlay('pmi')}
          disabled={!isSceneReady || !hasPmi}
        >
          <PMIIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      <Divider orientation="vertical" h={4} />
      {/* Reset View Tool */}
      <Tooltip
        placement="bottom"
        label={<ViewerButtonTooltip firstLine="Reset view" />}
      >
        <ViewerButton
          aria-label="Reset view"
          onClick={onResetView}
          disabled={!isSceneReady}
        >
          <ResetViewIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      {/* Controls Info Tool */}
      <ViewerPopoverButton
        title="Viewer controls"
        icon={<OutlineCircleQuestionIcon boxSize={3} />}
      >
        <DxfViewerControlsInfoRows isMobile={isMobile} />
      </ViewerPopoverButton>
      <DxfDisplayOptionsPopover />
    </ViewerToolsBar>
  );
};
